import { useState, useCallback } from "react";
import { AdjustmentMode } from "@/components/orders/OrderAdjustmentDialog";
import { asignUser } from "@/api/order-api";
import { toast } from "./use-toast";

interface UseOrderDialogsReturn {
  // Invoice dialog
  isOpenningInvoiceDialog: boolean;
  setIsOpenningInvoiceDialog: (open: boolean) => void;
  autoPrint: boolean;
  setAutoPrint: (autoPrint: boolean) => void;
  handleDownloadInvoice: () => void;
  handlePrintInvoice: () => void;

  // Receipt dialog
  isOpenningReceipt: boolean;
  setIsOpenningReceipt: (open: boolean) => void;
  handleShowReceipt: () => void;

  // Payment dialog
  openPaymentDialog: boolean;
  setOpenPaymentDialog: (open: boolean) => void;
  isCOD: boolean;
  setIsCOD: (isCOD: boolean) => void;
  handleCapturePaymentClick: () => void;
  handleCodClick: () => void;

  // Adjustment dialog
  showAdjustDialog: boolean;
  setShowAdjustDialog: (open: boolean) => void;
  adjustMode: AdjustmentMode;
  setAdjustMode: (mode: AdjustmentMode) => void;
  openRefund: () => void;
  openReturn: () => void;
  showAssignToUserDialog: boolean;
  setShowAssignToUserDialog: (open: boolean) => void;
  openAssignToUser: () => void;
  onAssignToUser: (userId: string, orderId: string) => Promise<void>;
}

export function useOrderDialogs(): UseOrderDialogsReturn {
  // Invoice dialog state
  const [isOpenningInvoiceDialog, setIsOpenningInvoiceDialog] =
    useState<boolean>(false);
  const [autoPrint, setAutoPrint] = useState<boolean>(false);
  const [showAssignToUserDialog, setShowAssignToUserDialog] = useState<boolean>(false);

  // Receipt dialog state
  const [isOpenningReceipt, setIsOpenningReceipt] = useState<boolean>(false);

  // Payment dialog state
  const [openPaymentDialog, setOpenPaymentDialog] = useState<boolean>(false);
  const [isCOD, setIsCOD] = useState<boolean>(false);

  // Adjustment dialog state
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [adjustMode, setAdjustMode] = useState<AdjustmentMode>("REFUND");

  const handleDownloadInvoice = useCallback(() => {
    setAutoPrint(false);
    setIsOpenningInvoiceDialog(true);
  }, []);

  const handlePrintInvoice = useCallback(() => {
    setAutoPrint(true);
    setIsOpenningInvoiceDialog(true);
  }, []);

  const handleShowReceipt = useCallback(() => {
    setIsOpenningReceipt(true);
  }, []);

  const handleCapturePaymentClick = useCallback(() => {
    setIsCOD(false);
    setOpenPaymentDialog(true);
  }, []);

  const handleCodClick = useCallback(() => {
    setIsCOD(true);
    setOpenPaymentDialog(true);
  }, []);

  const openRefund = useCallback(() => {
    setAdjustMode("REFUND");
    setShowAdjustDialog(true);
  }, []);

  const openReturn = useCallback(() => {
    setAdjustMode("RETURN");
    setShowAdjustDialog(true);
  }, []);

  const openAssignToUser = useCallback(() => {
    setShowAssignToUserDialog(true);
  }, []);
  const onAssignToUser = useCallback(async (userId: string, orderId: string) => {

    const resp = await asignUser(orderId, userId);
    if (resp.success) {
      setShowAssignToUserDialog(false);
      toast({ title: "Success", description: resp.message, variant: "default" });
    }
    else {
      toast({ title: "Error", description: resp.message, variant: "destructive" });

    }
    return Promise.resolve();
  }, []);

  return {
    // Invoice dialog
    isOpenningInvoiceDialog,
    setIsOpenningInvoiceDialog,
    autoPrint,
    setAutoPrint,
    handleDownloadInvoice,
    handlePrintInvoice,

    // Receipt dialog
    isOpenningReceipt,
    setIsOpenningReceipt,
    handleShowReceipt,

    // Payment dialog
    openPaymentDialog,
    setOpenPaymentDialog,
    isCOD,
    setIsCOD,
    handleCapturePaymentClick,
    handleCodClick,

    // Adjustment dialog
    showAdjustDialog,
    setShowAdjustDialog,
    adjustMode,
    setAdjustMode,
    openRefund,
    openReturn,
    showAssignToUserDialog,
    setShowAssignToUserDialog,
    openAssignToUser,
    onAssignToUser,
  };
}

